import _ from "lodash";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import UIBaseContentEditable from "UIComponents/UIBaseContentEditable";
import PropTypes from "prop-types";
import { colors } from "Constants/colorConstants";

import classes from "./NoteV2.scss";
import AIInputBox from "../AIInputBox";
import { aiGenerateActionsMapping } from "../AIInputBox/utils";
import { isNewNotesUi } from "Post/utils";

const BLANK_OBJECT = {};
const NoteV2 = props => {
  const {
    ASK_AI_NOTE_CAPTION,
    ASK_AI_ATTACHMENT_CAPTION_FILLED,
  } = aiGenerateActionsMapping;

  const {
    updateNote,
    value,
    noteTextAreaStyles,
    noteName,
    placeholder,
    onBlur,
    linesContainerStyles,
    contentEditableRef,
    setShowPromptBoxOverOptions,
    setShowPromptBox,
    showPromptBox,
    showPromptBoxOverOptions,
    getTextAreaTitle,
    setTextAreaTitle,
    textInputRef,
    setTextInputRef,
    parentContainerRef,
    isAIResponseGenerating,
    testId,
    configureAIVoiceProps,
  } = props;

  const isNewNoteUi = isNewNotesUi();
  const [isFocus, setFocus] = useState(false);
  const { t } = useTranslation(["common", "journal"]);
  const aiInputRef = useRef(null);
  const promptBoxRef = useRef(null);
  const noteContainerRef = useRef(null);
  const aiButtonStyle = {
    borderRadius: "4px",
    background: colors.white,
    boxShadow: "0px 2px 4px 0px rgba(0, 46, 57, 0.15)",
  };
  const handleTextSelection = event => {
    if (event.target === textInputRef?.domElm) {
      aiInputRef.current?.handleTextSelection(event);
    }
  };
  const style = {
    promptBoxContainer: {
      marginTop: "12px",
    },
    fontStyle: {
      fontWeight: "500",
    },
  };
  return (
    <div
      className={
        isNewNoteUi ? classes.noteLinesContainerV2 : classes.noteLinesContainer
      }
      style={{
        ...linesContainerStyles,
        minHeight:
          _.trim(value) &&
          textInputRef?.domElm?.clientHeight > 284 &&
          !showPromptBox
            ? `${
                textInputRef?.domElm?.clientHeight + 60 //height of AI outline button
              }px`
            : "",
      }}
      ref={noteContainerRef}
    >
      <div
        className={
          isNewNoteUi ? classes.noteContainerV2 : classes.noteContainer
        }
        onSelect={handleTextSelection}
      >
        <UIBaseContentEditable
          value={value}
          ref={ref => setTextInputRef(ref)}
          mode={"edit"}
          placeholder={placeholder}
          onFocusInputField={() => {
            textInputRef?.focus();
            setFocus(true);
          }}
          onBlurInputField={() => {
            onBlur();
            setTimeout(() => {
              setFocus(false);
            }, 300);
          }}
          updateInputField={updateNote}
          updateInputRef={ref => (contentEditableRef.current = ref)}
          id="text-area-input"
          name={noteName}
          shouldParseInPlainText={false}
          onKeyDown={event => {
            if ((event.metaKey || event.ctrlKey) && event.key === "j") {
              event.preventDefault();
              setShowPromptBoxOverOptions(true);
              setShowPromptBox(true);
              aiInputRef?.current?.setDisplayOptionsDefault();
            }
          }}
          shouldPasteAsPlainText={true}
          styleStripType={"CONTENT_EDITABLE"}
          style={noteTextAreaStyles}
          tagName={"span"}
          editContainerStyle={{ height: "fit-content", minHeight: "24px" }}
          containerStyle={{ height: "fit-content", minHeight: "24px" }}
          testId={testId}
        />
      </div>
      <div
        className={classes.promptBoxContainer}
        style={{
          width: showPromptBox || showPromptBoxOverOptions ? "100%" : "",
          bottom: showPromptBox || showPromptBoxOverOptions ? "0px" : "",
          top: showPromptBox || showPromptBoxOverOptions ? "0px" : "",
        }}
        ref={promptBoxRef}
      >
        <AIInputBox
          showPromptBox={showPromptBox}
          setShowPromptBox={setShowPromptBox}
          showPromptBoxOverOptions={showPromptBoxOverOptions}
          setShowPromptBoxOverOptions={setShowPromptBoxOverOptions}
          aiInputRef={aiInputRef}
          textAreaTitle={value}
          setTextAreaTitle={setTextAreaTitle}
          updateTextAreaInputLocally={updateNote}
          enableChatGPTPoweredTextFeatures={true}
          isFocussedRef={isFocus}
          textInputRef={textInputRef}
          contentEditableRef={contentEditableRef}
          getTextAreaTitle={getTextAreaTitle}
          showMagicWand={false}
          aiIconVariant={"plain-subtle"}
          aiButtonStyle={aiButtonStyle}
          placeholder={t("journal:note_prompt_placeholder")}
          askAIPromptAction={
            value ? ASK_AI_ATTACHMENT_CAPTION_FILLED : ASK_AI_NOTE_CAPTION
          }
          isNote={true}
          customPromptBoxStyles={
            showPromptBox ? style.promptBoxContainer : BLANK_OBJECT
          }
          parentContainerRef={parentContainerRef}
          responseStyle={style.fontStyle}
          configureAIVoiceProps={configureAIVoiceProps}
        />
      </div>
    </div>
  );
};

NoteV2.defaultProps = {
  ...UIBaseComponent.defaultProps,
  noteTextAreaStyles: {},
  minRows: 10,
  noteName: "Title",
  linesContainerStyles: {},
  autoFocus: false,
  isFocused: false,
};

NoteV2.propTypes = {
  ...UIBaseComponent.propTypes,
  minRows: PropTypes.number,
  noteTextAreaStyles: PropTypes.object,
  linesContainerStyles: PropTypes.object,
  updateNote: PropTypes.func,
  noteName: PropTypes.string,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onBlur: PropTypes.func,
  autoFocus: PropTypes.bool,
  isFocused: PropTypes.bool,
  configureAIVoiceProps: PropTypes.object,
};

export default NoteV2;
